import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@hopskipdrive/react-components';
import { analyticsEditedRider } from '@rideiq/api/services/Analytics';
import { ApiResponse } from '@rideiq/api/utils/responseTransform';
import { useFlags } from '@rideiq/contexts/LDContext';
import { Button } from '@rideiq/shared/components/Button';
import { useToastStore } from '@rideiq/shared/stores/toastStore';

import { RiderProfileForm } from './RiderProfileForm';
import { IRiderProfileFormType } from './types';

interface EditRiderProfileProps {
  id?: string;
  riderDetails?: IRiderProfileFormType;
  saveProfileEdit: (riderFormData) => Promise<ApiResponse<{
    message?: string | undefined;
  }> | null>;
}

export const EditRiderProfile: FC<EditRiderProfileProps> = ({ id, riderDetails, saveProfileEdit }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { resetToasts, addToast } = useToastStore();
  const navigate = useNavigate();
  const { iepStructuredNotesEnabled } = useFlags();
  const useNewForm = iepStructuredNotesEnabled;

  const handleClick = () => {
    if (useNewForm && id) {
      navigate(`/riders/${id}/edit`);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleSaveProfileEdit = async (formRiderDetails) => {
    const response = await saveProfileEdit(formRiderDetails);

    if (response?.type === 'success') {
      if (response?.data.message) {
        response.data.message.includes('DOB')
          ? setIsConfirmationModalOpen(true)
          : addToast({ severity: 'info', message: response.data.message });
      } else {
        addToast({ severity: 'success', message: 'Rider profile updated.' });
      }
      analyticsEditedRider(formRiderDetails?.slug, formRiderDetails, riderDetails);

      setIsModalOpen(false);
    } else {
      response?.error?.errors &&
        addToast({
          severity: 'error',
          message: (
            <>
              <p>{'Rider profile cannot be updated.'}</p>
              {response?.error.errors.map((message, index) => (
                <p key={`error-message-${index}`}>{`• ${message}`}</p>
              ))}
            </>
          ),
        });
    }
  };

  useEffect(() => {
    if (!isModalOpen) {
      resetToasts();
      setIsSubmitting(false);
    }
  }, [isModalOpen]);

  return (
    <>
      {!useNewForm && (
        <Presentation
          isOpen={isModalOpen}
          riderDetails={riderDetails}
          onCancel={() => setIsModalOpen(false)}
          onSubmit={handleSaveProfileEdit}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
        />
      )}
      <Button
        data-testid="edit-rider-profile-btn"
        color="secondary"
        size="small"
        onClick={handleClick}
        disabled={!riderDetails}
        style={{ minWidth: '68px', height: '25px' }}
      >
        Edit
      </Button>
      {isConfirmationModalOpen && <ConfirmationMessageModal close={() => setIsConfirmationModalOpen(false)} />}
    </>
  );
};

const Presentation = ({ isOpen, riderDetails, onCancel, onSubmit, isSubmitting, setIsSubmitting }) => (
  <Modal open={isOpen} maxWidth="xl" data-testid="edit-rider-profile">
    <Modal.Title>Edit Rider</Modal.Title>
    <Modal.Content>
      <RiderProfileForm
        riderDetails={riderDetails}
        onSubmit={onSubmit}
        setIsSubmitting={setIsSubmitting}
        pauseState={riderDetails?.state === 'paused'}
        mustBeMetState={riderDetails?.mustBeMet}
      />
    </Modal.Content>
    <Modal.Actions>
      <Button color="tertiary" data-testid="edit-rider-cancel-button" onClick={onCancel} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button autoFocus color="primary" type="submit" form={'rider-basic-info'} disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Save'}
      </Button>
    </Modal.Actions>
  </Modal>
);

const ConfirmationMessageModal = ({ close }) => (
  <Modal open={true} onClose={close}>
    <Modal.Title>Confirm safety settings</Modal.Title>
    <Modal.Content>
      {
        "Since the rider's date of birth changed, we adjusted their safety settings to our default recommendations. Please double check the rider's new safety settings."
      }
    </Modal.Content>
    <Modal.Actions>
      <Button color="primary" onClick={close}>
        Ok
      </Button>
    </Modal.Actions>
  </Modal>
);
